import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Grid,
  Breadcrumbs,
  Link
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PageContainer from '../../components/container/PageContainer';
import LineNameLabel from '../../components/common/LineNameLabel';
import { getBoards } from '../../reducers/kanban/boardActions';
import { useToken } from '../../hooks/useToken';
import KanbanContactsSidebar from '../../components/apps/kanban/KanbanContactsSidebar';
import SupportKanbanView from './SupportKanbanView';
import GeneralKanbanView from './GeneralKanbanView';
import FlujoBotKanbanView from './FlujoBotKanbanView';

// Componente para mostrar el estado de carga
const LoadingState = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
    <CircularProgress />
  </Box>
);

// Componente para mostrar error al cargar el tablero
const ErrorState = ({ error, navigate }) => (
  <Box textAlign="center" my={5}>
    <Typography variant="h4" color="error" gutterBottom>
      Error al cargar el tablero
    </Typography>
    <Typography variant="body1" color="textSecondary" paragraph>
      {error}
    </Typography>
    <Button variant="contained" color="primary" onClick={() => navigate('/admin/kanban')}>
      Volver a la lista de tableros
    </Button>
  </Box>
);

// Componente para mostrar mensaje cuando no se encuentra el tablero
const BoardNotFoundState = ({ navigate }) => (
  <Box textAlign="center" my={5}>
    <Typography variant="h4" color="textSecondary" gutterBottom>
      Tablero no encontrado
    </Typography>
    <Button variant="contained" color="primary" onClick={() => navigate('/admin/kanban')}>
      Volver a la lista de tableros
    </Button>
  </Box>
);

// Componente para mostrar el contenido del tablero
const BoardContent = ({ board, boardId }) => {
  // Normalizar el tipo de tablero para asegurar compatibilidad
  const normalizedBoardType = board?.board_type === 'support' ? 'soporte' : board?.board_type;

  // Determinar si se debe mostrar la barra lateral de contactos
  const showContactSidebar = board && (normalizedBoardType === 'general' || normalizedBoardType === 'soporte');

  // Determinar si se debe mostrar la vista Kanban
  const showKanbanView = board && (normalizedBoardType === 'general' || normalizedBoardType === 'soporte' || normalizedBoardType === 'flujo_bot');

  return (
    <Grid container spacing={2}>
      {/* Sidebar de contactos (solo para tableros tipo general o soporte) */}
      {showContactSidebar && (
        <Grid item xs={12} md={3} lg={3}>
          <KanbanContactsSidebar boardId={boardId} boardType={normalizedBoardType} />
        </Grid>
      )}

      {/* Contenido principal del tablero */}
      <Grid item xs={12} md={showContactSidebar ? 9 : 12} lg={showContactSidebar ? 9 : 12}>
        {showKanbanView ? (
          normalizedBoardType === 'soporte' ? (
            <SupportKanbanView
              boardId={boardId}
              isDetailView={true}
              board={board}
            />
          ) : normalizedBoardType === 'flujo_bot' ? (
            <FlujoBotKanbanView
              boardId={boardId}
              isDetailView={true}
              board={board}
            />
          ) : (
            <GeneralKanbanView
              boardId={boardId}
              isDetailView={true}
              board={board}
            />
          )
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography variant="h6" color="text.secondary">
              Este tipo de tablero no soporta vista Kanban
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

// Componente principal para la vista detallada del tablero
const BoardDetail = () => {
  // Hooks y estados
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();
  const agent = useSelector(state => state.agent);
  // Adaptación a la nueva estructura del estado después de la refactorización
  const { 
    boards: { 
      items: boards = [], 
      loading: boardsLoading,
      selectedBoardId,
      selectedBoard
    } = { items: [], loading: false, selectedBoardId: null, selectedBoard: null }
  } = useSelector(state => state.kanban);
  
  // Usar selectedBoardId de Redux en lugar de useParams
  const boardId = selectedBoardId;

  // Estados del componente
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(selectedBoard);
  const [loadingError, setLoadingError] = useState(null);
  // Ref instead of state — prevents useCallback/useEffect recreation on each API call
  const isLoadingFromAPIRef = useRef(false);

  // useRef para controlar si el componente está montado
  const isMountedRef = useRef(true);

  // Efecto para manejar cuando no hay boardId seleccionado
  useEffect(() => {
    if (!boardId) {
      // Si no hay tablero seleccionado, redirigir a la lista de tableros
      navigate('/admin/kanban');
      return;
    }
  }, [boardId, navigate]);

  // Efecto para sincronizar selectedBoard de Redux con estado local
  useEffect(() => {
    if (selectedBoard && String(selectedBoard.id) === String(boardId)) {
      setBoard(selectedBoard);
      setLoading(false);
      setLoadingError(null);
    }
  }, [selectedBoard, boardId]);

  // Preparar información para el PageContainer y Breadcrumb
  const agentName = agent?.name || 'Usuario';
  const customerName = agent?.id_customer ? `Cliente ${agent.id_customer}` : '';
  const boardTitle = board?.name || 'Tablero';
  const pageTitle = `${boardTitle} - ${agentName}`;

  // Breadcrumb personalizado para manejar navegación
  const [BCrumb, setBCrumb] = useState([
    {
      title: 'Inicio',
      onClick: true
    },
    {
      title: 'Lista de Tableros',
      onClick: true
    },
    {
      title: 'Cargando...',
    },
  ]);

  /**
   * Handler personalizado para navegación del breadcrumb
   * Evita conflictos con otras navegaciones programáticas
   */
  const handleBreadcrumbNavigation = (event, itemTitle) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (itemTitle === 'Inicio') {
      navigate('/admin/chat');
    } else if (itemTitle === 'Lista de Tableros') {
      navigate('/admin/kanban');
    }
  };

  // Función para cargar el tablero desde la API
  const loadBoardFromAPI = useCallback(async (boardIdStr) => {
    if (isLoadingFromAPIRef.current || !isMountedRef.current) {
      return;
    }

    try {
      isLoadingFromAPIRef.current = true;

      // Obtener el token de autenticación
      const token = await getTokenSilent();

      // Verificar si el componente sigue montado
      if (!isMountedRef.current) return;

      // Obtener los IDs del cliente y agente
      const id_customer = agent?.id_customer;
      const id_agent = agent?._id || agent?.id;

      if (!id_customer || !id_agent) {
        if (isMountedRef.current) {
          setLoadingError('No se pudo obtener información necesaria del cliente o agente');
          setLoading(false);
        }
        return;
      }

      // Cargar todos los tableros de la API
      const result = await dispatch(getBoards({
        id_customer,
        id_agent,
        limit: 100,
        token
      }));

      // Verificar si el componente sigue montado
      if (!isMountedRef.current) return;

      // Usar la respuesta directamente en lugar de confiar en el estado actualizado
      if (result?.payload) {
        const updatedBoards = Array.isArray(result.payload) ? result.payload : [];

        // Buscar el tablero por ID
        const foundBoard = updatedBoards.find(b => String(b.id) === boardIdStr);

        if (foundBoard) {
          if (isMountedRef.current) {
            setBoard(foundBoard);
            setLoadingError(null);
          }
        } else {
          if (isMountedRef.current) {
            setLoadingError(`No se encontró el tablero con ID: ${boardIdStr}`);
          }
        }
      } else {
        if (isMountedRef.current) {
          setLoadingError('Error al obtener datos del servidor');
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setLoadingError(`Error: ${error.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      isLoadingFromAPIRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, dispatch, getTokenSilent]);

  // useEffect de cleanup para establecer isMountedRef al desmontar
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Efecto para actualizar el breadcrumb cuando cambie el tablero
  useEffect(() => {
    if (board && isMountedRef.current) {
      setBCrumb(prev => [
        ...prev.slice(0, 2),
        { title: board.name }
      ]);
    }
  }, [board]);

  // Efecto para cargar los datos del tablero
  useEffect(() => {
    // Si no hay ID de tablero, mostrar error
    if (!boardId) {
      if (isMountedRef.current) {
        setLoadingError('ID de tablero no proporcionado');
        setLoading(false);
      }
      return;
    }

    // Convertir el ID a string para comparación consistente
    const boardIdStr = String(boardId);
    let isTableroSolicitado = false;

    const loadBoardData = async () => {
      // Evitamos buscar el mismo tablero más de una vez por ciclo
      if (isTableroSolicitado || !isMountedRef.current) {
        return;
      }

      isTableroSolicitado = true;

      if (isMountedRef.current) {
        setLoading(true);
      }

      try {
        if (boards?.length > 0) {
          // Buscar el tablero por ID en los datos ya cargados
          const foundBoard = boards.find(b => String(b.id) === boardIdStr);

          if (foundBoard) {
            if (isMountedRef.current) {
              setBoard(foundBoard);
              setLoadingError(null);
              setLoading(false);
            }
          } else {
            if (isMountedRef.current) {
              await loadBoardFromAPI(boardIdStr);
            }
          }
        } else {
          if (isMountedRef.current) {
            await loadBoardFromAPI(boardIdStr);
          }
        }
      } catch (error) {
        if (isMountedRef.current) {
          setLoadingError('Error al cargar los datos del tablero');
          setLoading(false);
        }
      }
    };

    loadBoardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, boards]);

  // Efecto para manejar cuando no hay tablero después de la carga
  useEffect(() => {
    if (!loading && !loadingError && !board && isMountedRef.current) {
      setLoadingError('No se pudo encontrar el tablero solicitado.');
    }
  }, [loading, loadingError, board]);

  /**
   * Componente breadcrumb personalizado que maneja la navegación
   * sin conflictos con otras funciones de navegación
   */
  const CustomBreadcrumb = ({ title, items }) => (
    <Grid
      container
      sx={{
        padding: '2px',
        paddingLeft: "15px",
        marginBottom: '2px'
      }}
    >
      <Grid item xs={12} sm={6} lg={8}>
        <Typography
          fontWeight="700"
          variant="h3"
          sx={{
            lineHeight: '1.235',
          }}
        >
          {title}
        </Typography>

        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          {items.map((item) => (
            <div key={item.title}>
              {item.onClick ? (
                <Link 
                  underline="none" 
                  color="inherit" 
                  component="button"
                  onClick={(e) => handleBreadcrumbNavigation(e, item.title)}
                  sx={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 'inherit' }}
                >
                  {item.title}
                </Link>
              ) : (
                <Typography color="textPrimary">{item.title}</Typography>
              )}
            </div>
          ))}
        </Breadcrumbs>
      </Grid>
    </Grid>
  );

  // Renderizado condicional basado en el estado
  const renderContent = () => {
    // Once we have the board, always render it — prevents unmounting child views on loading state changes
    if (board) {
      const normalizedBoardType = board.board_type === 'support' ? 'soporte' : board.board_type;
      return (
        <BoardContent board={{ ...board, board_type: normalizedBoardType }} boardId={boardId} />
      );
    }

    if (loading || boardsLoading) {
      return <LoadingState />;
    }

    if (loadingError) {
      return <ErrorState error={loadingError} navigate={navigate} />;
    }

    return <BoardNotFoundState navigate={navigate} />;
  };

  return (
    <PageContainer title={pageTitle} description={customerName || "Vista detallada del tablero Kanban"}>
      <CustomBreadcrumb title={<LineNameLabel/>} items={BCrumb} />
      <Box mt={1}>
        {renderContent()}
      </Box>
    </PageContainer>
  );
};

export default BoardDetail;